// Web twin of composeApp's ActiveAnalysesStrip.kt — same entry shape, same card and list-row
// visuals, kept in sync by hand since Compose HTML and Compose Multiplatform can't share widgets.

package com.islamicmedia.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.islamicmedia.shared.domain.model.AnalysesLayout
import com.islamicmedia.shared.domain.model.MediaType
import com.islamicmedia.shared.domain.model.verdictLabel
import com.islamicmedia.shared.presentation.AnalysisStripEntry
import com.islamicmedia.shared.presentation.TaskStatus
import com.islamicmedia.site.theme.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/** Verdict tiers for the "Your analyses" verdict filter — mirror the strip status labels
 *  (score >= 7 Safe, >= 4 Caution, else Not Safe). */
private enum class VerdictFilter(val label: String) { SAFE("Minor"), CAUTION("Moderate"), NOT_SAFE("Major") }

/** Which of the two filter pills is expanded (only one at a time). */
private enum class OpenFilterPill { NONE, TYPE, VERDICT }

/** Type-filter options (first is the All reset). Short labels since space is tight at grid width. */
private val TYPE_OPTIONS: List<Pair<String, MediaType?>> = listOf(
    "All" to null,
    "Books" to MediaType.BOOKS,
    "Movies" to MediaType.MOVIES,
    "TV Shows" to MediaType.TV_SHOWS,
    "YouTube" to MediaType.YOUTUBE,
    "Games" to MediaType.GAMES,
)

/** Verdict-filter options (first is the All reset). */
private val VERDICT_OPTIONS: List<Pair<String, VerdictFilter?>> = listOf(
    "All" to null,
    "Minor" to VerdictFilter.SAFE,
    "Moderate" to VerdictFilter.CAUTION,
    "Major" to VerdictFilter.NOT_SAFE,
)

private fun verdictOf(score: Int?): VerdictFilter? = score?.let {
    when {
        it >= 7 -> VerdictFilter.SAFE
        it >= 4 -> VerdictFilter.CAUTION
        else -> VerdictFilter.NOT_SAFE
    }
}

/**
 * Inline strip showing the user's analyses on the search screen — running, this-session
 * completions, and persisted history merged into a single 2-col grid. Tap a card to enter
 * its progress / results screen. RUNNING cards expose an inline cancel button.
 */
@Composable
fun ActiveAnalysesStrip(
    entries: List<AnalysisStripEntry>,
    /**
     * True while the remote history sync is still running. On a FIRST login the local cache is
     * empty, so without this the whole strip is absent for the several seconds the sync takes and
     * the user has no idea anything is coming. With no entries yet this renders a "Loading…" line
     * where the cards will go; it clears once the sync settles — including when it finds nothing,
     * so a user with no history isn't left staring at it.
     */
    loading: Boolean = false,
    /** How densely to lay the cards out — the user's Settings choice (see [AnalysesLayout]). */
    layout: AnalysesLayout = AnalysesLayout.GRID_2,
) {
    if (entries.isEmpty() && !loading) return

    // Nothing has arrived yet — show the header + a "Loading…" line and hide the filter pills,
    // which would be dead controls over an empty grid.
    val loadingOnly = entries.isEmpty()

    var selectedType by remember { mutableStateOf<MediaType?>(null) }
    var selectedVerdict by remember { mutableStateOf<VerdictFilter?>(null) }
    var openPill by remember { mutableStateOf(OpenFilterPill.NONE) }

    Div(attrs = {
        style {
            width(100.percent)
            marginTop(14.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(10.px)
        }
    }) {
        // Two filter pills with the "YOUR ANALYSES" label centered between them (saves a row).
        // Type (left, grows right) + verdict (right, grows left); only one expands at a time. The
        // collapsed trio stays mounted and fades; each expanded track slides in as an absolute
        // overlay, revealed by an animatable clip-path — so close mirrors open (no snap).
        Div(attrs = { style { position(Position.Relative); width(100.percent) } }) {
            // Collapsed trio (both pills + centered label) — fades out while a pill is open.
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    width(100.percent)
                    opacity(if (openPill == OpenFilterPill.NONE) 1 else 0)
                    property("pointer-events", if (openPill == OpenFilterPill.NONE) "auto" else "none")
                    property("transition", "opacity 0.16s ease")
                }
            }) {
                if (!loadingOnly) {
                    CollapsedPill(
                        label = if (selectedType == null) "All types"
                                else TYPE_OPTIONS.first { it.second == selectedType }.first,
                        active = selectedType != null,
                    ) { openPill = OpenFilterPill.TYPE }
                }
                Div(attrs = { style { property("flex", "1") } })
                Div(attrs = {
                    style {
                        fontSize(11.px)
                        fontWeight(600)
                        color(Color.white)
                        letterSpacing(0.5.px)
                        property("text-transform", "uppercase")
                        property("white-space", "nowrap")
                    }
                }) { Text("Your analyses") }
                Div(attrs = { style { property("flex", "1") } })
                if (!loadingOnly) {
                    CollapsedPill(
                        label = selectedVerdict?.label ?: "All verdicts",
                        active = selectedVerdict != null,
                    ) { openPill = OpenFilterPill.VERDICT }
                }
            }
            // Type track — grows rightward from the left edge.
            FilterOverlay(open = !loadingOnly && openPill == OpenFilterPill.TYPE, fromStart = true) {
                OptionsTrack(TYPE_OPTIONS, selectedType) { selectedType = it; openPill = OpenFilterPill.NONE }
            }
            // Verdict track — grows leftward from the right edge.
            FilterOverlay(open = !loadingOnly && openPill == OpenFilterPill.VERDICT, fromStart = false) {
                OptionsTrack(VERDICT_OPTIONS, selectedVerdict) { selectedVerdict = it; openPill = OpenFilterPill.NONE }
            }
        }

        val filtered = entries.filter {
            (selectedType == null || it.mediaType == selectedType) &&
                (selectedVerdict == null || verdictOf(it.score) == selectedVerdict)
        }
        if (loadingOnly) {
            Div(attrs = {
                style { fontSize(13.px); property("color", "rgba(255,255,255,0.7)"); paddingLeft(4.px) }
            }) { Text("Loading…") }
        } else if (filtered.isEmpty()) {
            Div(attrs = {
                style { fontSize(13.px); property("color", "rgba(255,255,255,0.7)"); paddingLeft(4.px) }
            }) { Text("No matching analyses yet") }
        } else {
            AnalysisCardGrid(filtered, layout)
        }
    }
}

/** Absolute overlay hosting an expanded options track, revealed by a clip-path wipe. [fromStart]
 *  true wipes left→right (type pill), false right→left (verdict pill). The wipe is symmetric, so the
 *  close animation is the exact reverse of the open — the fix for the pills snapping shut. */
@Composable
private fun FilterOverlay(open: Boolean, fromStart: Boolean, content: @Composable () -> Unit) {
    val clipClosed = if (fromStart) "inset(0 100% 0 0)" else "inset(0 0 0 100%)"
    Div(attrs = {
        style {
            position(Position.Absolute)
            left(0.px); right(0.px)
            top(50.percent)
            property("transform", "translateY(-50%)")
            opacity(if (open) 1 else 0)
            property("pointer-events", if (open) "auto" else "none")
            property("clip-path", if (open) "inset(0 0 0 0)" else clipClosed)
            property("transition", "clip-path 0.24s ease, opacity 0.18s ease")
        }
    }) { content() }
}

/** A collapsed filter pill showing the current selection + a caret. Green when a non-All value
 *  is active, otherwise the light track color — matching the expanded options track. */
@Composable
private fun CollapsedPill(label: String, active: Boolean, onClick: () -> Unit) {
    Div(attrs = {
        onClick { onClick() }
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            padding(6.px, 14.px)
            borderRadius(999.px)
            cursor("pointer")
            property("white-space", "nowrap")
            property("transition", TransitionMedium)
            backgroundColor(if (active) Primary else Color("#F1F3F4"))
            color(if (active) Color("#F1F3F4") else Gray500)
            fontSize(12.px)
            fontWeight(600)
        }
    }) {
        Span { Text(label) }
    }
}

/** Expanded options: the full-width light track with one flex chip per option; the selected
 *  one is a green pill with track-colored text. Same styling as the pills. */
@Composable
private fun <T> OptionsTrack(
    options: List<Pair<String, T?>>,
    selected: T?,
    onSelect: (T?) -> Unit,
) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            width(100.percent)
            gap(2.px)
            borderRadius(999.px)
            backgroundColor(Color("#F1F3F4"))
            padding(2.px)
        }
    }) {
        options.forEach { (label, value) ->
            val isSelected = selected == value
            Div(attrs = {
                onClick { onSelect(value) }
                style {
                    property("flex", "1")
                    property("text-align", "center")
                    property("white-space", "nowrap")
                    padding(6.px, 4.px)
                    borderRadius(999.px)
                    cursor("pointer")
                    fontSize(12.px)
                    fontWeight(if (isSelected) 700 else 500)
                    property("transition", TransitionMedium)
                    backgroundColor(if (isSelected) Primary else Color.transparent)
                    color(if (isSelected) Color("#F1F3F4") else Gray500)
                }
            }) { Text(label) }
        }
    }
}

/**
 * Shared grid layout used by both the search-screen strip and the dedicated History view, so
 * cards look identical wherever they appear. [layout] is the user's Settings choice — LIST is
 * still a CSS grid (one column) rather than a different container, so switching layouts never
 * needs a different wrapper, only a different item count and card.
 */
@Composable
fun AnalysisCardGrid(entries: List<AnalysisStripEntry>, layout: AnalysesLayout = AnalysesLayout.GRID_2) {
    Div(attrs = {
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(${layout.columns}, minmax(0, 1fr))")
            // Rows sit tighter than posters — there's no card padding of their own to separate them.
            gap(if (layout == AnalysesLayout.LIST) 8.px else 10.px)
            width(100.percent)
        }
    }) {
        entries.forEach { entry ->
            if (layout == AnalysesLayout.LIST) EntryRow(entry = entry) else EntryCard(entry = entry)
        }
    }
}

@Composable
private fun EntryCard(entry: AnalysisStripEntry) {
    val accent = entry.mediaType.cssAccent()
    val statusTint = statusColor(entry).toString()

    // Pull a trailing "(YYYY)" off the title so the year can join the author line below.
    val yearMatch = Regex("""\s*\((\d{4})\)\s*$""").find(entry.title)
    val bareTitle = if (yearMatch != null) entry.title.removeRange(yearMatch.range).trim() else entry.title
    val meta = listOfNotNull(
        entry.author?.takeIf { entry.mediaType == MediaType.BOOKS && it.isNotBlank() },
        yearMatch?.groupValues?.get(1)
    ).joinToString(" • ")

    Div(attrs = {
        onClick { entry.onClick() }
        style {
            position(Position.Relative)
            width(100.percent)
            property("aspect-ratio", "2 / 3")
            borderRadius(16.px)
            // Verdict now reads from the card's own chrome rather than a text pill.
            property("border", "4px solid $statusTint")
            property("box-sizing", "border-box")
            property("overflow", "hidden")
            cursor("pointer")
            property("background", "linear-gradient(135deg, ${accent}73 0%, ${accent}2E 100%)")
            property("box-shadow", "0 4px 14px rgba(0,0,0,0.18)")
            property("transition", "transform 0.18s ease, box-shadow 0.18s ease")
        }
        onMouseOver {
            (it.currentTarget as? org.w3c.dom.HTMLElement)?.let { el ->
                el.style.transform = "translateY(-2px)"
                el.style.boxShadow = "0 8px 22px rgba(0,0,0,0.28)"
            }
        }
        onMouseOut {
            (it.currentTarget as? org.w3c.dom.HTMLElement)?.let { el ->
                el.style.transform = "translateY(0)"
                el.style.boxShadow = "0 4px 14px rgba(0,0,0,0.18)"
            }
        }
    }) {
        // Cover art layer (background)
        if (entry.coverUrl != null) {
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("inset", "0")
                    property("background-image", "url(\"${entry.coverUrl}\")")
                    property("background-size", "cover")
                    property("background-position", "center")
                }
            })
        } else {
            // Fallback: large centered emoji on the accent gradient
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("inset", "0")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(54.px)
                    property("filter", "drop-shadow(0 2px 6px rgba(0,0,0,0.25))")
                }
            }) { Text(entry.mediaType.emoji) }
        }

        // Bottom darkening gradient for legibility
        Div(attrs = {
            style {
                property("position", "absolute")
                property("inset", "0")
                property(
                    "background",
                    "linear-gradient(to bottom, rgba(0,0,0,0) 45%, rgba(0,0,0,0.92) 100%)"
                )
            }
        })

        // Info container — translucent over cover art at the bottom
        Div(attrs = {
            style {
                property("position", "absolute")
                property("left", "0")
                property("right", "0")
                property("bottom", "0")
                padding(12.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(5.px)
            }
        }) {
            Div(attrs = {
                style {
                    property("font-family", TitleFontFamily)
                    fontSize(14.px)
                    fontWeight(600)
                    color(Color.white)
                    property("line-height", "1.2")
                    property("display", "-webkit-box")
                    property("-webkit-line-clamp", "2")
                    property("-webkit-box-orient", "vertical")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("text-shadow", "0 1px 3px rgba(0,0,0,0.6)")
                }
            }) { Text(bareTitle) }

            // Author (books) + year, joined by a dot.
            if (meta.isNotBlank()) {
                Div(attrs = {
                    style {
                        fontSize(11.px)
                        fontWeight(500)
                        property("color", "rgba(255,255,255,0.85)")
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("text-shadow", "0 1px 2px rgba(0,0,0,0.55)")
                    }
                }) { Text(meta) }
            }

            // Verdict tier lives on the border now; status text (analyzing/failed/cancelled)
            // still needs a pill because the border can't express it. (The type pill moved to the
            // top-left.)
            if (entry.status != TaskStatus.COMPLETE) {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        property("margin-top", "3px")
                    }
                }) {
                    CardTag(verdictLabel(entry), bg = statusTint, fg = "#FFFFFF")
                }
            }
        }

        // Type pill (top-LEFT), with the quick-check badge stacked directly under it when this was
        // a quick search. Both sit opposite the overflow menu / cancel button, which own the
        // top-right corner.
        Div(attrs = {
            style {
                property("position", "absolute")
                property("top", "8px")
                property("left", "8px")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.FlexStart)
                gap(4.px)
                // Keep the stack clear of the top-right menu even with a long label.
                property("max-width", "calc(100% - 52px)")
            }
        }) {
            CardTag(entry.mediaType.singularLabel, bg = "#F5F3EC", fg = "#171717", maxWidth = "100%")
            if (entry.kidScan) {
                Span(attrs = {
                    style {
                        padding(2.px, 10.px); borderRadius(999.px); property("background", "#E6F7F6")
                        property("box-shadow", "0 1px 4px rgba(0,0,0,0.2)"); property("align-self", "flex-start")
                        property("display", "inline-flex")
                    }
                }) { KidzTag() }
            }
            // Mirrors the "Quick check" tab pill: yellow background, stopwatch icon, italic label.
            if (entry.quickCheck) {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(4.px)
                        padding(3.px, 8.px)
                        borderRadius(999.px)
                        property("background", "#FACC15")
                        property("box-shadow", "0 1px 4px rgba(0,0,0,0.25)")
                    }
                }) {
                    Img(src = "/images/quick.png", attrs = {
                        style {
                            height(13.px)
                            property("width", "auto")
                        }
                    })
                    Span(attrs = {
                        style {
                            fontSize(10.px)
                            fontWeight(700)
                            property("font-style", "italic")
                            property("color", "#4B5563")
                            property("white-space", "nowrap")
                        }
                    }) { Text("Quick check") }
                }
            }
        }

        // Overflow menu (top-right) — Pin / Hide. Translucent so it sits lightly over the cover
        // art. Hidden while an analysis is running; the cancel button owns that corner instead.
        val onTogglePin = entry.onTogglePin
        val onHide = entry.onHide
        if (entry.onCancel == null && (onTogglePin != null || onHide != null)) {
            var menuOpen by remember { mutableStateOf(false) }
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("top", "8px"); property("right", "8px")
                    property("z-index", "5")
                }
            }) {
                Button(attrs = {
                    onClick { it.stopPropagation(); menuOpen = !menuOpen }
                    style {
                        width(28.px); height(28.px)
                        borderRadius(50.percent)
                        // Off-white disc with dark dots, matching the pin badge below it — the two
                        // top-right affordances read as one family rather than light-on-dark vs
                        // dark-on-light. (Mirrors composeApp's ActiveAnalysesStrip.)
                        property("background", "#FAFAFA")
                        property("border", "none")
                        cursor("pointer")
                        fontSize(15.px); fontWeight(700)
                        property("color", "#404040")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                        property("line-height", "1")
                        property("padding", "0")
                    }
                }) { Text("⋯") }

                if (menuOpen) {
                    // Click-catcher so the menu closes on an outside click without swallowing
                    // the card's own onClick underneath it.
                    Div(attrs = {
                        onClick { it.stopPropagation(); menuOpen = false }
                        style {
                            property("position", "fixed"); property("inset", "0")
                            property("z-index", "10")
                        }
                    })
                }
                // Rendered UNCONDITIONALLY, unlike the click-catcher above: a CSS transition needs
                // the element to already exist to animate from, and needs it to survive so the
                // close animates too. Hidden state is opacity 0 + scale 0.6 + pointer-events none,
                // not removal from the DOM. (Mirrors the AnimatedVisibility in composeApp.)
                Div(attrs = {
                    onClick { it.stopPropagation() }
                    // menu-list turns on the inset hairlines between rows (CSS in Index.kt),
                    // the same mechanism the profile menu uses.
                    classes("menu-list")
                    style {
                        property("position", "absolute")
                        // Top-right corner sits ON the ⋯ button's, so the menu reads as
                        // unfolding out of the button rather than hanging below it. (The
                        // wrapper div is already positioned at the button, hence 0/0.)
                        property("top", "0"); property("right", "0")
                        property("z-index", "11")
                        backgroundColor(Color.white)
                        borderRadius(10.px)
                        property("box-shadow", "0 6px 20px rgba(0,0,0,0.18)")
                        // Sized to the longest label plus padding — not a guessed fixed width,
                        // which left "Hide"/"Pin" swimming in space. (composeApp uses the Compose
                        // equivalent, Modifier.width(IntrinsicSize.Max).)
                        property("width", "max-content")
                        property("overflow", "hidden")
                        // Grow out of the button: pivot on the corner the menu shares with it.
                        property("transform-origin", "top right")
                        property("transform", if (menuOpen) "scale(1)" else "scale(0.6)")
                        opacity(if (menuOpen) 1 else 0)
                        property("pointer-events", if (menuOpen) "auto" else "none")
                        // Quicker out than in — a dismissal that lingers feels unresponsive.
                        property("transition", "transform 0.16s ease, opacity 0.11s ease")
                    }
                }) {
                    if (onHide != null) {
                        Div(attrs = {
                            onClick { it.stopPropagation(); menuOpen = false; onHide() }
                            classes("menu-item")
                            style {
                                padding(12.px, 12.px); fontSize(14.px)
                                color(Gray700); cursor("pointer")
                            }
                        }) { Text("Hide") }
                    }
                    // No explicit divider element — the .menu-list CSS draws an inset hairline
                    // before every row but the first, so it can't get out of step with the
                    // profile menu.
                    if (onTogglePin != null) {
                        Div(attrs = {
                            onClick { it.stopPropagation(); menuOpen = false; onTogglePin() }
                            classes("menu-item")
                            style {
                                padding(12.px, 12.px); fontSize(14.px)
                                color(Gray700); cursor("pointer")
                            }
                        }) { Text(if (entry.pinnedAtMs != null) "Unpin" else "Pin") }
                    }
                }
            }
        }

        // Pin indicator — sits under the menu button so a pinned card is obvious at a glance.
        // Same off-white disc as the ⋯ button above it: the pin glyph is dark and warm, so it needs
        // a light backing to read against cover art rather than muddying into it.
        if (entry.pinnedAtMs != null) {
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("top", "42px"); property("right", "8px")
                    // Explicitly BELOW the menu's stacking context (z-index 5 on its wrapper), so
                    // an open menu is never painted over by the pin badge on a pinned card.
                    property("z-index", "1")
                    width(24.px); height(24.px)
                    borderRadius(50.percent)
                    property("background", "#FAFAFA")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    fontSize(11.px)
                }
            }) {
                Img(src = "/images/pin.png", attrs = {
                    attr("alt", "Pinned")
                    style { height(13.px); property("width", "auto") }
                })
            }
        }

        // Cancel affordance (top-right) for RUNNING
        val onCancel = entry.onCancel
        if (onCancel != null) {
            Button(attrs = {
                onClick {
                    it.stopPropagation()
                    onCancel()
                }
                style {
                    property("position", "absolute")
                    property("top", "8px")
                    property("right", "8px")
                    width(28.px)
                    height(28.px)
                    borderRadius(50.percent)
                    property("background", "rgba(0,0,0,0.55)")
                    property("border", "none")
                    cursor("pointer")
                    fontSize(12.px)
                    color(Color.white)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("backdrop-filter", "blur(6px)")
                    property("-webkit-backdrop-filter", "blur(6px)")
                }
            }) { Text("✕") }
        }

        // Progress bar overlaid at the bottom edge for RUNNING
        if (entry.status == TaskStatus.RUNNING) {
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("left", "0")
                    property("right", "0")
                    property("bottom", "0")
                    height(3.px)
                    property("background", "rgba(255,255,255,0.25)")
                    property("overflow", "hidden")
                }
            }) {
                Div(attrs = {
                    style {
                        height(100.percent)
                        width(((entry.progressFraction.coerceIn(0f, 1f)) * 100f).percent)
                        property("background", "#FFFFFF")
                        property("transition", "width 0.4s ease")
                    }
                })
            }
        }
    }
}

/**
 * The text-first row: thumbnail, title | year, menu. Web twin of composeApp's AnalysisListRow.
 *
 * This exists because the poster card's cover art is its whole height — at two across that
 * artwork dominates the screen, and people with a long catalogue asked to see more titles at
 * once. Three across only shrinks the same card; this inverts the emphasis instead, so the title
 * is the thing you read and the cover is a small identifier beside it.
 *
 * The verdict still reads from the border colour, exactly as it does on the poster card — it is
 * the one piece of information that would otherwise be lost in the shrink, and repeating it as
 * text would cost the row the space this layout exists to reclaim.
 */
@Composable
private fun EntryRow(entry: AnalysisStripEntry) {
    val accent = entry.mediaType.cssAccent()
    val statusTintColor = statusColor(entry)
    val statusTint = statusTintColor.toString()

    // Same split the poster card does: a trailing "(YYYY)" is pulled off the title so the year
    // can sit in its own slot rather than inside the name. Same regex as EntryCard, so list and
    // grid never disagree about what counts as the trailing year.
    val yearMatch = Regex("""\s*\((\d{4})\)\s*$""").find(entry.title)
    val bareTitle = if (yearMatch != null) entry.title.removeRange(yearMatch.range).trim() else entry.title
    val year = yearMatch?.groupValues?.get(1)

    Div(attrs = {
        onClick { entry.onClick() }
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            height(72.px)
            borderRadius(12.px)
            backgroundColor(Color.white)
            property("border", "3px solid $statusTint")
            property("box-sizing", "border-box")
            cursor("pointer")
            property("overflow", "hidden")
        }
    }) {
        // Cover thumbnail — inset so it reads as a small poster beside the row rather than
        // filling it. Falls back to the same tinted gradient + emoji the poster card uses.
        Div(attrs = {
            style {
                property("margin", "6px")
                property("height", "calc(100% - 12px)")
                property("aspect-ratio", "2 / 3")
                borderRadius(8.px)
                property("overflow", "hidden")
                property("flex-shrink", "0")
                if (entry.coverUrl != null) {
                    property("background-image", "url(\"${entry.coverUrl}\")")
                    property("background-size", "cover")
                    property("background-position", "center")
                } else {
                    property("background", "linear-gradient(135deg, ${accent}73 0%, ${accent}2E 100%)")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(20.px)
                }
            }
        }) {
            if (entry.coverUrl == null) Text(entry.mediaType.emoji)
        }

        // Title | year, then author/status underneath — the verdict itself stays on the border,
        // same as the poster card, since at row height there's no room to repeat it as text.
        Div(attrs = {
            style {
                property("flex", "1")
                property("min-width", "0")
                padding(0.px, 4.px, 0.px, 0.px)
            }
        }) {
            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center) } }) {
                Div(attrs = {
                    style {
                        property("font-family", TitleFontFamily)
                        fontSize(14.px)
                        fontWeight(600)
                        color(Gray900)
                        property("flex", "1")
                        property("min-width", "0")
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    }
                }) { Text(bareTitle) }
                // The separator belongs to the year, not the title — printed only once there IS
                // a year, so a row without one doesn't end in a bare pipe.
                if (year != null) {
                    Div(attrs = {
                        style {
                            fontSize(13.px)
                            fontWeight(500)
                            color(Gray500)
                            property("white-space", "nowrap")
                            // Padding, not spaces in the string: HTML collapses runs of whitespace,
                            // so the mobile version's "  |  " would render here as a single space
                            // either side and the two platforms would not match.
                            property("padding-left", "8px")
                        }
                    }) { Text("| $year") }
                }
            }
            // Author for books, or the status line for anything not finished — the poster card
            // shows the same pairing, and a running row with no progress text reads as stalled.
            val sub = listOfNotNull(
                entry.author?.takeIf { entry.mediaType == MediaType.BOOKS && it.isNotBlank() },
                if (entry.status != TaskStatus.COMPLETE) verdictLabel(entry) else null,
            ).joinToString(" • ")
            if (sub.isNotEmpty()) {
                Div(attrs = {
                    style {
                        fontSize(11.px)
                        fontWeight(500)
                        color(if (entry.status != TaskStatus.COMPLETE) statusTintColor else Gray500)
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("margin-top", "2px")
                    }
                }) { Text(sub) }
            }
        }

        // Pin indicator — same glyph as the poster card, just inline instead of an overlay badge.
        if (entry.pinnedAtMs != null) {
            Img(src = "/images/pin.png", attrs = {
                attr("alt", "Pinned")
                style {
                    height(13.px)
                    property("width", "auto")
                    property("margin-right", "4px")
                    property("flex-shrink", "0")
                }
            })
        }

        // Same menu as the poster card, and hidden mid-run for the same reason: pinning
        // something that hasn't finished has nothing to pin.
        val onTogglePin = entry.onTogglePin
        val onHide = entry.onHide
        if (entry.onCancel == null && (onTogglePin != null || onHide != null)) {
            var menuOpen by remember { mutableStateOf(false) }
            Div(attrs = {
                style {
                    property("position", "relative")
                    property("margin", "0 8px")
                    property("flex-shrink", "0")
                }
            }) {
                Button(attrs = {
                    onClick { it.stopPropagation(); menuOpen = !menuOpen }
                    style {
                        width(28.px); height(28.px)
                        borderRadius(50.percent)
                        property("background", "#FAFAFA")
                        property("border", "none")
                        cursor("pointer")
                        fontSize(15.px); fontWeight(700)
                        property("color", "#404040")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                        property("line-height", "1")
                        property("padding", "0")
                    }
                }) { Text("⋯") }

                if (menuOpen) {
                    // Click-catcher so the menu closes on an outside click without swallowing
                    // the row's own onClick underneath it.
                    Div(attrs = {
                        onClick { it.stopPropagation(); menuOpen = false }
                        style {
                            property("position", "fixed"); property("inset", "0")
                            property("z-index", "10")
                        }
                    })
                }
                // Rendered UNCONDITIONALLY, unlike the click-catcher above, so the CSS transition
                // has an element to animate to/from on both open and close.
                Div(attrs = {
                    onClick { it.stopPropagation() }
                    // menu-list turns on the inset hairlines between rows (CSS in Index.kt), the
                    // same mechanism the profile menu and the poster card's own menu use.
                    classes("menu-list")
                    style {
                        property("position", "absolute")
                        property("top", "0"); property("right", "0")
                        property("z-index", "11")
                        backgroundColor(Color.white)
                        borderRadius(10.px)
                        property("box-shadow", "0 6px 20px rgba(0,0,0,0.18)")
                        property("width", "max-content")
                        property("overflow", "hidden")
                        property("transform-origin", "top right")
                        property("transform", if (menuOpen) "scale(1)" else "scale(0.6)")
                        opacity(if (menuOpen) 1 else 0)
                        property("pointer-events", if (menuOpen) "auto" else "none")
                        property("transition", "transform 0.16s ease, opacity 0.11s ease")
                    }
                }) {
                    if (onHide != null) {
                        Div(attrs = {
                            onClick { it.stopPropagation(); menuOpen = false; onHide() }
                            classes("menu-item")
                            style {
                                padding(12.px, 12.px); fontSize(14.px)
                                color(Gray700); cursor("pointer")
                            }
                        }) { Text("Hide") }
                    }
                    if (onTogglePin != null) {
                        Div(attrs = {
                            onClick { it.stopPropagation(); menuOpen = false; onTogglePin() }
                            classes("menu-item")
                            style {
                                padding(12.px, 12.px); fontSize(14.px)
                                color(Gray700); cursor("pointer")
                            }
                        }) { Text(if (entry.pinnedAtMs != null) "Unpin" else "Pin") }
                    }
                }
            }
        } else {
            Div(attrs = { style { width(12.px); property("flex-shrink", "0") } })
        }
    }
}

private fun MediaType.cssAccent(): String {
    val rgb = (activeColorHex and 0xFFFFFFL).toString(16).padStart(6, '0')
    return "#$rgb"
}

/** Small rounded tag on a card — type (off-white, top-left) or status (status color, bottom).
 *  [maxWidth] is a percentage of the tag's own container: 62% keeps a long status pill off the
 *  card edge, but inside the shrink-to-fit top-left stack a percentage would resolve against the
 *  stack's own content width and clip the label, so that caller passes 100%. */
@Composable
private fun CardTag(text: String, bg: String, fg: String, maxWidth: String = "62%") {
    Span(attrs = {
        style {
            padding(3.px, 9.px)
            borderRadius(999.px)
            fontSize(10.px)
            fontWeight(700)
            property("background", bg)
            property("color", fg)
            property("white-space", "nowrap")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("max-width", maxWidth)
            property("box-shadow", "0 1px 4px rgba(0,0,0,0.2)")
        }
    }) { Text(text) }
}

/** Short verdict/status word for the right-hand tag. */
private fun verdictLabel(entry: AnalysisStripEntry): String = when (entry.status) {
    TaskStatus.RUNNING -> "Analyzing ${(entry.progressFraction * 100).toInt().coerceIn(0, 99)}%"
    TaskStatus.COMPLETE -> entry.score?.let {
        verdictLabel(it, entry.concernCount, short = true) ?: "Done"
    } ?: "Done"
    TaskStatus.ERROR -> "Failed"
    TaskStatus.CANCELLED -> "Cancelled"
}

private fun statusColor(entry: AnalysisStripEntry): CSSColorValue = when (entry.status) {
    TaskStatus.RUNNING -> Primary
    TaskStatus.COMPLETE -> {
        val score = entry.score
        when {
            score == null -> Gray500
            score >= 7 -> ScoreGreen
            score >= 4 -> ScoreAmber
            else -> ScoreRed
        }
    }
    TaskStatus.ERROR -> ScoreRed
    TaskStatus.CANCELLED -> Gray500
}
